import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import * as XLSX from "xlsx";
import useDataStore from "../../store/dataStore";

const BASE_URL = "https://mis.empowerplus.in";

const emptyForm = {
    departmentId: "",
    departmentName: "",
    projectId: "",
    epCenterId: "",
};

export default function ProjectCenterManagementPage() {
    const token = localStorage.getItem("token");
    const store = useDataStore();
    const CACHE_KEY = "projectCenters";

    /* ================= COMMON ================= */
    const [msg, setMsg] = useState({ type: "", text: "" });
    const flash = (type, text) => {
        setMsg({ type, text });
    };

    const authHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}` }),
        [token]
    );

    const jsonHeaders = useMemo(
        () => ({ Authorization: `Bearer ${token}`, "Content-Type": "application/json" }),
        [token]
    );

    const safeJson = async (res) => {
        const text = await res.text();
        try { return text ? JSON.parse(text) : null; }
        catch { return text; }
    };

    /* ================= FETCH ================= */
    const [rows, setRows] = useState(() => store.getRows(CACHE_KEY));
    const [loading, setLoading] = useState(false);
    const [loadingAll, setLoadingAll] = useState(false);
    const [allLoaded, setAllLoaded] = useState(() => store.isAllLoaded(CACHE_KEY));
    const [search, setSearch] = useState("");
    const [visibleCount, setVisibleCount] = useState(100);

    const fetchFirst100 = async () => {
        if (store.isCached(CACHE_KEY)) {
            setRows(store.getRows(CACHE_KEY));
            setAllLoaded(store.isAllLoaded(CACHE_KEY));
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${BASE_URL}/api/project-center-management`, { headers: authHeaders });
            const json = await safeJson(res);
            const items = Array.isArray(json) ? json : (json?.content ?? []);
            setRows(items);
            setAllLoaded(true);
            store.setRows(CACHE_KEY, items, true);
        } catch {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllRows = async () => {
        setLoadingAll(true);
        try {
            const res = await fetch(`${BASE_URL}/api/project-center-management`, { headers: authHeaders });
            const data = await safeJson(res);
            const all = Array.isArray(data) ? data : (data?.content ?? []);
            setRows(all);
            setAllLoaded(true);
            setVisibleCount(all.length);
            store.setRows(CACHE_KEY, all, true);
        } catch {
        } finally {
            setLoadingAll(false);
        }
    };

    useEffect(() => { fetchFirst100(); }, []);

    /* ================= SEARCH ================= */
    useEffect(() => { setVisibleCount(100); }, [search]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return rows;
        return rows.filter(
            (r) =>
                r.pcId?.toLowerCase().includes(q) ||
                r.departmentId?.toLowerCase().includes(q) ||
                r.departmentName?.toLowerCase().includes(q) ||
                r.projectId?.toLowerCase().includes(q) ||
                r.epCenterId?.toLowerCase().includes(q)
        );
    }, [rows, search]);

    /* ================= UPLOAD ================= */
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const CHUNK_SIZE = 500;
    const PARALLEL = 2;

    const uploadExcel = async () => {
        if (!file) return flash("err", "Select an Excel file first");
        let dataRows;
        try {
            const buffer = await file.arrayBuffer();
            const wb = XLSX.read(buffer, { type: "array" });
            const ws = wb.Sheets[wb.SheetNames[0]];
            const raw = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
            dataRows = raw.slice(1);
        } catch (err) {
            return flash("err", `Cannot read file: ${err.message}`);
        }
        if (!dataRows.length) return flash("err", "Excel file is empty");
        const totalRows = dataRows.length;
        const totalChunks = Math.ceil(totalRows / CHUNK_SIZE);
        setUploading(true);
        setUploadProgress({ uploaded: 0, total: totalRows, chunk: 0, totalChunks });
        let uploadedSoFar = 0;
        let totalInserted = 0;
        let totalSkipped = 0;
        let failed = false;
        let firstError = "";
        const uploadOneChunk = async (i) => {
            const chunkRows = dataRows.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
            const res = await fetch(`${BASE_URL}/api/project-center-management/upload-json?actor=Rahul`, {
                method: "POST",
                headers: { ...authHeaders, "Content-Type": "application/json" },
                body: JSON.stringify(chunkRows),
            });
            if (res.status === 413) throw new Error(`Chunk ${i + 1}: Payload too large (413)`);
            const text = await res.text();
            let data;
            try { data = JSON.parse(text); } catch { data = text; }
            if (!res.ok) throw new Error(`Chunk ${i + 1}: ${data?.message || `HTTP ${res.status}`}`);
            return { inserted: data?.inserted ?? 0, skipped: data?.skipped ?? 0 };
        };
        for (let batchStart = 0; batchStart < totalChunks && !failed; batchStart += PARALLEL) {
            const batchEnd = Math.min(batchStart + PARALLEL, totalChunks);
            const batchIndices = Array.from({ length: batchEnd - batchStart }, (_, k) => batchStart + k);
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks });
            const results = await Promise.allSettled(batchIndices.map(uploadOneChunk));
            for (const result of results) {
                if (result.status === "fulfilled") {
                    totalInserted += result.value.inserted;
                    totalSkipped += result.value.skipped;
                    uploadedSoFar += result.value.inserted + result.value.skipped;
                } else {
                    failed = true;
                    firstError = result.reason?.message || "Upload failed";
                }
            }
            setUploadProgress({ uploaded: uploadedSoFar, total: totalRows, chunk: batchEnd, totalChunks, error: failed });
        }
        setUploading(false);
        if (!failed) {
            flash("ok", `Upload complete — Inserted: ${totalInserted.toLocaleString()}, Skipped: ${totalSkipped.toLocaleString()} (${totalChunks} chunks)`);
            setFile(null);
            setUploadProgress(null);
            store.invalidate(CACHE_KEY);
            fetchAllRows();
        } else {
            flash("err", firstError);
            setUploadProgress(prev => prev ? { ...prev, error: true } : null);
        }
    };

    const downloadTemplate = () => {
        const a = document.createElement("a");
        a.href = "/templates/ProjectCenterManagement.xlsx";
        a.download = "ProjectCenterManagement.xlsx";
        document.body.appendChild(a);
        a.click();
        a.remove();
    };

    /* ================= EXPORT ================= */
    const [exporting, setExporting] = useState(false);

    const exportExcel = async () => {
        if (exporting) return;
        setExporting(true);
        try {
            let data = rows;
            if (!allLoaded) {
                const res = await fetch(`${BASE_URL}/api/project-center-management`, { headers: authHeaders });
                const json = await safeJson(res);
                data = Array.isArray(json) ? json : (json?.content ?? []);
                setRows(data);
                setAllLoaded(true);
                setVisibleCount(data.length);
                store.setRows(CACHE_KEY, data, true);
            }
            if (!data.length) return flash("err", "No data to export");
            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "ProjectCenterManagement");
            XLSX.writeFile(wb, "project_center_management.xlsx");
            flash("ok", `Exported ${data.length.toLocaleString()} records`);
        } catch (e) {
            flash("err", "Export failed: " + e.message);
        } finally {
            setExporting(false);
        }
    };

    /* ================= CREATE ================= */
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState(emptyForm);
    const [creating, setCreating] = useState(false);

    const createRow = async () => {
        setCreating(true);
        try {
            const res = await fetch(`${BASE_URL}/api/project-center-management`, {
                method: "POST",
                headers: jsonHeaders,
                body: JSON.stringify(createForm),
            });
            const data = await safeJson(res);
            if (!res.ok) {
                flash("err", data?.message || "Create failed");
                return;
            }
            flash("ok", "Record created");
            setCreateOpen(false);
            setCreateForm(emptyForm);
            store.invalidate(CACHE_KEY);
            fetchAllRows();
        } catch (err) {
            console.error("[ProjectCenter Create] Error:", err);
            flash("err", "Create failed — check console");
        } finally {
            setCreating(false);
        }
    };

    /* ================= EDIT ================= */
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const openEdit = (row) => {
        setEditingId(row.pcId);
        setForm({ ...emptyForm, ...row });
    };

    const updateRow = async () => {
        const res = await fetch(
            `${BASE_URL}/api/project-center-management?pcId=${encodeURIComponent(editingId)}`,
            {
                method: "PUT",
                headers: jsonHeaders,
                body: JSON.stringify({
                    departmentId: form.departmentId,
                    departmentName: form.departmentName,
                    projectId: form.projectId,
                    epCenterId: form.epCenterId,
                }),
            }
        );
        if (res.ok) {
            flash("ok", "Record updated");
            setEditingId(null);
            store.invalidate(CACHE_KEY);
            fetchAllRows();
        } else {
            const data = await safeJson(res);
            flash("err", data?.message || "Update failed");
        }
    };

    /* ================= DELETE ================= */
    const deleteRow = async (pcId) => {
        if (!window.confirm("Delete this record?")) return;
        const res = await fetch(
            `${BASE_URL}/api/project-center-management?pcId=${encodeURIComponent(pcId)}`,
            { method: "DELETE", headers: authHeaders }
        );
        if (res.ok) {
            flash("ok", "Record deleted");
            store.invalidate(CACHE_KEY);
            fetchAllRows();
        } else {
            flash("err", "Delete failed");
        }
    };

    const fields = [
        { key: "departmentId", label: "Department ID" },
        { key: "departmentName", label: "Department Name" },
        { key: "projectId", label: "Project ID" },
        { key: "epCenterId", label: "EP Center ID" },
    ];

    /* ================= UI ================= */
    return (
        <div className="cmp-page">
            <div className="header">
                <h1>Project Center Management</h1>
            </div>

            {msg.text && (
                <div className={`flash ${msg.type === "ok" ? "flash-ok" : "flash-err"}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span>{msg.text}</span>
                    <button onClick={() => setMsg({ type: "", text: "" })} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, fontWeight: "bold", lineHeight: 1, color: "inherit", opacity: 0.6, padding: "0 2px" }}>×</button>
                </div>
            )}

            {uploadProgress && (
                <div style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "14px 18px", marginBottom: 8 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                        <span style={{ fontSize: 13, fontWeight: 800, color: "#1e3a8a" }}>
                            {uploadProgress.error
                                ? `⚠ Stopped at chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}`
                                : uploading
                                    ? `Uploading chunk ${uploadProgress.chunk} of ${uploadProgress.totalChunks}…`
                                    : `Upload complete`}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#6b7280" }}>
                            {uploadProgress.uploaded.toLocaleString()} / {uploadProgress.total.toLocaleString()} rows
                            &nbsp;({Math.round((uploadProgress.uploaded / uploadProgress.total) * 100)}%)
                        </span>
                    </div>
                    <div style={{ height: 10, background: "#e5e7eb", borderRadius: 999, overflow: "hidden" }}>
                        <div style={{
                            height: "100%",
                            width: `${(uploadProgress.uploaded / uploadProgress.total) * 100}%`,
                            background: uploadProgress.error ? "#ef4444" : "linear-gradient(90deg,#1e40af,#3b82f6)",
                            borderRadius: 999,
                            transition: "width 0.3s ease",
                        }} />
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>
                        {uploadProgress.total.toLocaleString()} total rows · {uploadProgress.totalChunks} chunk{uploadProgress.totalChunks > 1 ? "s" : ""} of {CHUNK_SIZE} rows each
                    </div>
                </div>
            )}

            {/* Upload */}
            <div className="card grid-3">
                <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setFile(e.target.files[0] || null)}
                    style={{ border: "1px solid #e5e7eb", borderRadius: 10, padding: "10px 12px", background: "#fff" }}
                />
                <button className="btn btn-primary" onClick={uploadExcel} disabled={uploading}>
                    {uploading ? `Uploading chunk ${uploadProgress?.chunk ?? ""}/${uploadProgress?.totalChunks ?? ""}…` : "Upload Excel"}
                </button>
                <button className="btn btn-outline" onClick={downloadTemplate}>
                    Download Template
                </button>
            </div>

            {/* Table */}
            <div className="card">
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                    <input
                        className="search"
                        placeholder="Search PC ID / department / project / center..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        style={{ flex: "0 0 70%", marginBottom: 0 }}
                    />
                    <button
                        className="btn btn-outline"
                        onClick={exportExcel}
                        style={{ flex: "0 0 calc(15% - 8px)", fontSize: "0.8rem", padding: "6px 10px" }}
                        disabled={exporting}
                    >
                        {exporting ? "Processing..." : "Export All Data"}
                    </button>
                    {/* <button
                        className="btn btn-primary"
                        onClick={() => { setCreateForm(emptyForm); setCreateOpen(true); }}
                        style={{ flex: "0 0 15%", fontSize: "0.8rem", padding: "6px 10px" }}
                    >
                        + Add New
                    </button> */}
                </div>

                <div className="table-wrap">
                    <table className="ep-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>PC ID</th>
                                <th>Department ID</th>
                                <th>Department Name</th>
                                <th>Project ID</th>
                                <th>EP Center ID</th>
                                {/* <th className="actions">Actions</th> */}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.slice(0, visibleCount).map((r, i) => (
                                <tr key={r.pcId}>
                                    <td>{i + 1}</td>
                                    <td>{r.pcId}</td>
                                    <td>{r.departmentId}</td>
                                    <td>{r.departmentName}</td>
                                    <td>{r.projectId}</td>
                                    <td>{r.epCenterId}</td>
                                    <td className="actions">
                                        <button className="btn btn-sm" onClick={() => openEdit(r)}>
                                            Edit
                                        </button>
                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => deleteRow(r.pcId)}
                                        >
                                            Delete
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {!filtered.length && (
                                <tr>
                                    <td colSpan="7" className="muted">
                                        {loading ? "Loading..." : "No records found"}
                                    </td>
                                </tr>
                            )}
                            {!allLoaded && !loading && (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: "center", padding: "14px" }}>
                                        <button className="btn btn-outline" onClick={fetchAllRows} disabled={loadingAll}>
                                            {loadingAll ? "Loading all data…" : "Load All Data"}
                                        </button>
                                    </td>
                                </tr>
                            )}
                            {allLoaded && filtered.length > visibleCount && (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: "center", padding: "14px" }}>
                                        <button
                                            className="btn btn-outline"
                                            onClick={() => setVisibleCount((c) => c + 100)}
                                        >
                                            Show More ({filtered.length - visibleCount} remaining)
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* CREATE MODAL */}
            {createOpen &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setCreateOpen(false)}>
                        <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-title">Add Project Center Mapping</div>

                            {fields.map(({ key, label }) => (
                                <div className="field" key={key}>
                                    <label>{label}</label>
                                    <input
                                        value={createForm[key] ?? ""}
                                        onChange={(e) => setCreateForm((p) => ({ ...p, [key]: e.target.value }))}
                                    />
                                </div>
                            ))}

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setCreateOpen(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={createRow} disabled={creating}>
                                    {creating ? "Creating..." : "Create"}
                                </button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            {/* EDIT MODAL */}
            {editingId !== null &&
                createPortal(
                    <div className="modal-overlay" onClick={() => setEditingId(null)}>
                        <div className="ep-modal" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-title">Edit Project Center — {editingId}</div>

                            {fields.map(({ key, label }) => (
                                <div className="field" key={key}>
                                    <label>{label}</label>
                                    <input
                                        value={form[key] ?? ""}
                                        onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))}
                                    />
                                </div>
                            ))}

                            <div className="modal-actions">
                                <button className="btn" onClick={() => setEditingId(null)}>Cancel</button>
                                <button className="btn btn-primary" onClick={updateRow}>Save</button>
                            </div>
                        </div>
                    </div>,
                    document.body
                )}

            <style>{`
.cmp-page { padding: 22px; background: #f7f7fb; min-height: 100vh; font-family: Inter; }
.header h1 { margin: 0 0 14px; font-size: 26px; font-weight: 900; }

.flash { padding: 10px; border-radius: 10px; margin: 12px 0; font-weight: 700; }
.flash-ok  { background: #ecfdf5; color: #065f46; }
.flash-err { background: #fef2f2; color: #991b1b; }

.card { background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 16px; margin-bottom: 14px; }
.grid-3 { display: grid; grid-template-columns: 1fr auto auto; gap: 10px; }

.btn { border: 1px solid #e5e7eb; background: #fff; padding: 10px 14px; border-radius: 12px; font-weight: 800; cursor: pointer; font-size: 13px; }
.btn-primary { background: #2563eb; color: #fff; border-color: #2563eb; }
.btn-outline { background: #fff; }
.btn-sm { padding: 6px 10px; font-size: 12px; }
.btn-danger { border-color: #fca5a5; color: #991b1b; }
.btn:disabled { opacity: .6; cursor: not-allowed; }

.search { padding: 10px; border-radius: 12px; border: 1px solid #e5e7eb; width: 100%; box-sizing: border-box; }
.table-wrap { overflow-x: auto; }
.ep-table { width: 100%; border-collapse: collapse; }
.ep-table th, .ep-table td { padding: 10px; border-bottom: 1px solid #e5e7eb; font-size: 13px; white-space: nowrap; text-align: left; }
.ep-table th { color: #6b7280; }
.actions { display: flex; gap: 8px; }
.muted { color: #6b7280; font-weight: 700; text-align: center; }

.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: center; justify-content: center; z-index: 99999; }
.ep-modal { background: #fff; padding: 24px; border-radius: 16px; width: 480px; max-height: 90vh; overflow-y: auto; }
.modal-title { font-size: 18px; font-weight: 900; margin-bottom: 16px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 16px; }

.field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 10px; }
.field label { font-size: 12px; color: #6b7280; font-weight: 800; }
.field input { padding: 10px; border-radius: 12px; border: 1px solid #e5e7eb; font-size: 13px; }
            `}</style>
        </div>
    );
}
